import ignore, { Ignore } from 'ignore';
import { readFile, access } from 'fs/promises';
import { join } from 'path';
import { Config } from '../types/index.js';

/**
 * Ignore matcher that combines .gitignore, .mem2ignore, and config rules
 */
export class IgnoreMatcher {
  private ig: Ignore;
  private config: Config;
  private basePath: string;

  constructor(basePath: string, config: Config) {
    this.ig = ignore();
    this.basePath = basePath;
    this.config = config;
  }

  /**
   * Initialize the ignore matcher by loading all ignore sources
   */
  async initialize(): Promise<void> {
    // Add default ignore patterns from config
    this.ig.add(this.config.watch.defaultIgnore);
    this.ig.add(this.config.watch.additionalIgnore);

    // Load .mem2ignore if exists
    await this.loadIgnoreFile('.mem2ignore');

    // Load .gitignore if config allows
    if (this.config.watch.excludeGitignored) {
      await this.loadIgnoreFile('.gitignore');
    }

    // Never ignore MEMORY.md, CLAUDE.md, AGENTS.md themselves
    // (even if they're in .gitignore)
    this.ig.add(['!MEMORY.md', '!CLAUDE.md', '!AGENTS.md']);
  }

  /**
   * Load ignore patterns from a file
   */
  private async loadIgnoreFile(filename: string): Promise<void> {
    const filePath = join(this.basePath, filename);
    try {
      await access(filePath);
      const content = await readFile(filePath, 'utf-8');
      const lines = content
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line && !line.startsWith('#'));
      this.ig.add(lines);
    } catch {
      // File doesn't exist, skip
    }
  }

  /**
   * Check if a path should be ignored
   * @param path - Relative path from base directory
   * @returns true if path should be ignored
   */
  shouldIgnore(path: string): boolean {
    return this.ig.ignores(path);
  }

  /**
   * Filter an array of paths, removing ignored ones
   * @param paths - Array of relative paths
   * @returns Filtered array of paths
   */
  filter(paths: string[]): string[] {
    return paths.filter((path) => !this.shouldIgnore(path));
  }
}
